import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { useDefaultHomePath } from '@/hooks/use-default-home';
import { api } from '@/lib/api';

/**
 * Two-step login.
 *   Step 1: email → Continue → server returns { hasPassword, authMode }.
 *   Step 2: shows whichever methods apply for this user.
 *      - hasPassword=true  → password field + Sign In (+ Link as alternative)
 *      - hasPassword=false → just "Send Link" (first-time / passwordless users)
 *
 * The server's /auth/login-options endpoint returns the same shape whether
 * the email exists or not — non-existent emails still get the "Send Link"
 * UI, and requestMagicLink silently no-ops on unknown emails. So an
 * attacker can't enumerate accounts from the response.
 */
type AuthMode = 'password' | 'passwordless' | 'both';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, requestMagicLink, isAuthenticated, fetchMe } = useAuth();

  const [step, setStep] = useState<'email' | 'method'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [portalAccess, setPortalAccess] = useState(true);
  const [hasPassword, setHasPassword] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('both');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // When the user has both options available, the password field shows
  // first; this flag is a UI toggle to switch to the Link method.
  const [useLinkInstead, setUseLinkInstead] = useState(false);

  const defaultHome = useDefaultHomePath();
  const from = (location.state as any)?.from?.pathname || defaultHome;

  // Cross-tab sign-in re-fetch (kept from the prior implementation so a
  // sign-in on another tab restores this tab's session without retype).
  useEffect(() => {
    void fetchMe();
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('atlas-auth');
    const onMsg = (e: MessageEvent) => {
      const msg = e.data;
      if (msg && typeof msg === 'object' && msg.type === 'sign-in') void fetchMe();
    };
    ch.addEventListener('message', onMsg);
    return () => { ch.removeEventListener('message', onMsg); ch.close(); };
  }, [fetchMe]);

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from]);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await api<{ portalAccess: boolean; hasPassword: boolean; authMode: AuthMode }>('/auth/login-options', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      if (!r.success || !r.data) throw new Error(r.error?.message || 'Failed');
      setPortalAccess(r.data.portalAccess);
      setHasPassword(r.data.hasPassword);
      setAuthMode(r.data.authMode);
      // First-time users (no password yet) default to the link path so they
      // don't sit on an empty password field trying to guess one.
      setUseLinkInstead(!r.data.hasPassword);
      setStep('method');
    } catch (err: any) {
      setError(err.message || 'Could not look up account options.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestMagicLink(email);
      navigate('/auth/magic-link-sent', { state: { email } });
    } catch (err: any) {
      setError(err.message || 'Failed to send link');
    } finally {
      setLoading(false);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setPassword('');
    setError('');
    setUseLinkInstead(false);
  };

  // Method visibility, given what /login-options reported. `portalAccess`
  // gates ALL methods — without it, nothing is offered and the contact-
  // admin notice shows instead.
  const linkAvailable = portalAccess && (authMode === 'passwordless' || authMode === 'both');
  const passwordAvailable = portalAccess && hasPassword && (authMode === 'password' || authMode === 'both');
  const showPasswordForm = passwordAvailable && !useLinkInstead;
  const showLinkForm = (!passwordAvailable || useLinkInstead) && linkAvailable;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold font-heading text-foreground">Atlas Fleet Maintenance</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to your account</p>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        {step === 'email' && (
          <form onSubmit={handleContinue} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                required
                autoFocus
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="you@company.co.nz"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !email}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Checking…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'method' && (
          <>
            {/* Email banner — surfaces who we're signing in as + a way back. */}
            <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <span className="truncate">{email}</span>
              <button
                type="button"
                onClick={backToEmail}
                className="text-xs text-primary hover:underline"
              >
                Change
              </button>
            </div>

            {/* First-time-user hint — only shown when the lookup said no password
                and the link path is available. Reframes the screen as a setup
                step rather than a regular sign-in. */}
            {!hasPassword && linkAvailable && (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-foreground">
                You don't have a password set up yet. Click <strong>Send Link</strong> to receive a
                one-time sign-in link by email — once signed in, you can set a password under Profile.
              </div>
            )}

            {showPasswordForm && (
              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    required
                    autoFocus
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>
            )}

            {showLinkForm && (
              <form onSubmit={handleLink} className="space-y-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading ? 'Sending…' : 'Send Link'}
                </button>
              </form>
            )}

            {/* Method toggle — only when BOTH are actually available. */}
            {passwordAvailable && linkAvailable && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setUseLinkInstead((v) => !v); setError(''); }}
                  className="text-sm text-primary hover:underline"
                >
                  {useLinkInstead ? 'Use password instead' : 'Sign in with a Link instead'}
                </button>
              </div>
            )}

            {passwordAvailable && !useLinkInstead && (
              <div className="text-center">
                <a href="/auth/forgot-password" className="text-sm text-muted-foreground hover:text-foreground">
                  Forgot password?
                </a>
              </div>
            )}

            {/* No method available at all — admin-managed account in password-only
                mode but with no password yet. Tell the user to contact an admin. */}
            {!showPasswordForm && !showLinkForm && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-foreground">
                No sign-in method is available for this email. Please contact your administrator to be sent an invite.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
